import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  TrendingUp, Flame, ChevronDown, X,
  MessageCircle, Sparkles, Hash
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-1'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  GA01: {
    scene: '🏗️ 건축 현장. 설계사가 도면을 보며 설명하고 있다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '선생님, 도면에 점, 선, 면이 왜 이렇게 많이 나오나요?' },
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '모든 도형은 점→선→면 순서로 만들어져요! 점이 모여 선이 되고, 선이 모여 면이 되죠.' },
      { speaker: 'friend', emoji: '😮', name: '수아', text: '직선이랑 선분이랑 반직선은 다른 건가요?' },
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '직선은 양쪽으로 무한히, 반직선은 한 방향으로만 무한히, 선분은 두 끝점 사이에만 있어요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '직선: 양방향 무한 | 반직선: 한방향 무한 | 선분: 두 끝점 사이 (유한)' },
    ],
    reveal: '직선 AB ↔ | 반직선 AB → | 선분 AB •━━━•',
    revealEmoji: '📏',
  },
  GA02: {
    scene: '🧭 지도 수업. 방향각을 배우는 시간.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '각도 0°부터 360°까지 있는데, 예각·둔각은 어떻게 나눠요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '90°를 기준으로 나눠요! 90°보다 작으면 예각, 딱 90°면 직각, 90°~180° 사이면 둔각이에요.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '180°는요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '180°는 일직선이 되니까 "평각"이라고 해요. 각의 분류를 기억하면 도형 문제가 훨씬 쉬워요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '예각: 0°~90° 미만 | 직각: 90° | 둔각: 90°~180° 미만 | 평각: 180°' },
    ],
    reveal: '예각(0°<a<90°) | 직각(90°) | 둔각(90°<a<180°) | 평각(180°)',
    revealEmoji: '📐',
  },
  GA03: {
    scene: '🛤️ 기찻길 옆. 두 레일이 나란히 뻗어 있다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '기찻길 두 레일은 끝까지 만나지 않겠죠?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 이렇게 만나지 않는 두 직선을 "평행"하다고 해요. l∥m으로 표시하죠.' },
      { speaker: 'friend', emoji: '😄', name: '수아', text: '평행선을 가로지르는 선이 있으면 각도가 특별하게 되나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '네! 동위각(같은 위치 각)과 엇각(엇갈린 각)이 생겨요. 이 각들의 성질이 아주 중요해요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '평행: 같은 평면에서 만나지 않는 두 직선 | 수직: 90°로 만남 | l∥m, l⊥m 기호' },
    ],
    reveal: 'l∥m: 평행 | l⊥m: 수직 | 평행선은 같은 평면에서 만나지 않는 두 직선',
    revealEmoji: '🛤️',
  },
  GA04: {
    scene: '🏢 3D 모델링 수업. 직육면체를 만들고 있다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '직육면체 모서리들 관계가 복잡해요. 어떻게 분류하나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '두 직선의 관계는 세 가지예요! 한 점에서 만남, 평행, 그리고 꼬인 위치.' },
      { speaker: 'friend', emoji: '😮', name: '수아', text: '"꼬인 위치"가 뭐예요? 처음 듣는 말이에요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '공간에서만 있는 개념이에요! 만나지도 않고 평행하지도 않은 두 직선이에요. 직육면체 대각선 모서리들이 꼬인 위치예요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '공간에서 두 직선: ①한 점에서 만남 ②평행 ③꼬인 위치(만나지도 평행하지도 않음)' },
    ],
    reveal: '평면: 만남/평행만 존재 | 공간: 만남/평행/꼬인위치 세 가지 존재',
    revealEmoji: '📦',
  },
  GA05: {
    scene: '🔭 관측소. 평행한 두 지평선과 빛의 각도를 관측 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '평행선에서 각도 관계가 헷갈려요. 동위각이랑 엇각이 뭐가 달라요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '동위각은 같은 쪽에 있는 각, 엇각은 반대쪽에 엇갈려 있는 각이에요. 평행선에선 둘 다 크기가 같아요!' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '같은 쪽에 있는 각인 "동측 내각"은요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '동측 내각은 합이 180°가 돼요! 동위각·엇각은 "같다", 동측 내각은 "합이 180°". 이게 핵심이에요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '동위각=같다 | 엇각=같다 | 동측 내각=합 180° (평행선 조건)' },
    ],
    reveal: 'l∥m이면: 동위각=동위각, 엇각=엇각, 동측내각+동측내각=180°',
    revealEmoji: '🔭',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'GA01',
    title: '점·선·면의 개념',
    subtitle: '도형의 기본 요소인 점, 선, 면의 관계를 이해한다',
    icon: '📏',
    color: 'bg-sky-500',
    lightColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    textColor: 'text-sky-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['점·선·면', '직선', '선분', '반직선'],
    objective: '직선·반직선·선분의 차이를 구별하고, 두 점을 지나는 직선의 유일성을 이해한다.',
    concept: `**점·선·면의 기본 개념**

📌 도형의 기본 요소
• 점(Point): 위치만 있고 크기가 없음
• 선(Line): 점이 움직인 자취, 길이만 있고 넓이 없음
• 면(Plane): 선이 움직인 자취, 넓이만 있고 두께 없음

📌 직선·반직선·선분 비교
| 종류 | 기호 | 특징 |
|------|------|------|
| 직선 AB | ↔AB | 양쪽 무한 |
| 반직선 AB | →AB | A에서 B 방향 한쪽 무한 |
| 선분 AB | •AB• | A,B 사이 유한 (길이 있음) |

📌 직선의 결정
• 두 점을 지나는 직선: 오직 1개
• 한 점을 지나는 직선: 무수히 많음

📌 교점과 교선
• 교점: 두 직선(또는 선과 면)이 만나는 점
• 교선: 두 면이 만나는 선

⚠️ 주의
• 직선 AB = 직선 BA (방향 무관)
• 반직선 AB ≠ 반직선 BA (방향 중요!)
• 선분 AB = 선분 BA (방향 무관)`,
    keyPoints: [
      '직선: 양방향 무한 (끝점 없음)',
      '반직선: 한쪽만 무한 (시작점 있음)',
      '선분: 두 끝점 사이 (길이 잴 수 있음)',
      '두 점 결정 직선: 단 1개',
    ],
    examples: [
      {
        question: '다음을 구별하시오.\n① 양쪽 방향으로 무한히 뻗은 선\n② 점 A에서 시작해 점 B 방향으로 무한히 뻗은 선\n③ 점 A와 점 B 사이의 선',
        answer: '① 직선 AB  ② 반직선 AB  ③ 선분 AB',
        explanation: '① 끝없이 양쪽 → 직선\n② 한 점에서 시작해 한방향 무한 → 반직선\n③ 두 끝점 사이 → 선분',
      },
      {
        question: '다음 중 옳은 것을 고르시오.\n① 반직선 AB = 반직선 BA\n② 선분 AB = 선분 BA\n③ 두 점을 지나는 직선은 2개이다',
        answer: '② 선분 AB = 선분 BA',
        explanation: '① 반직선은 방향이 있어 AB ≠ BA\n② 선분은 방향 무관 → AB = BA ✓\n③ 두 점을 지나는 직선은 오직 1개',
      },
    ],
    practice: {
      question: '선분 AB의 중점 M에 대해 AM = 4cm일 때\n① AB의 길이를 구하시오.\n② BM의 길이를 구하시오.',
      answer: '① AB = 8cm  ② BM = 4cm',
      hint: '중점은 선분을 이등분. AM = MB이므로 AB = 2×AM',
      steps: [
        '① 중점 M은 AB를 이등분 → AM = MB = 4cm',
        '② AB = AM + MB = 4 + 4 = 8cm',
        '③ BM = MB = 4cm',
      ],
    },
    misconceptions: ['GE01'],
  },
  {
    id: 'GA02',
    title: '각의 종류와 측정',
    subtitle: '각도 범위에 따른 각의 분류와 맞꼭지각을 이해한다',
    icon: '📐',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['예각', '직각', '둔각', '맞꼭지각'],
    objective: '각의 종류를 각도 범위로 분류하고, 맞꼭지각의 성질을 이해한다.',
    concept: `**각의 종류**

📌 각도 범위별 분류
| 종류 | 범위 |
|------|------|
| 예각 | 0° < a < 90° |
| 직각 | a = 90° |
| 둔각 | 90° < a < 180° |
| 평각 | a = 180° |
| 우각 | 180° < a < 360° |

📌 맞꼭지각
• 두 직선이 만날 때 서로 마주보는 각
• 맞꼭지각의 크기는 서로 같다
• ∠AOC = ∠BOD (맞꼭지각)

📌 각의 이분선 (내각의 이등분선)
• 각을 두 등분하는 반직선

📌 실생활 예시
• 직각: 책상 모서리, 창문 틀
• 예각: 뾰족한 지붕
• 둔각: 기울어진 의자 등받이
• 평각: 일직선 도로

⚠️ 주의
• 90°는 예각 아님! 직각
• 180°는 둔각 아님! 평각`,
    keyPoints: [
      '예각: 0°보다 크고 90°보다 작다',
      '직각: 정확히 90°',
      '둔각: 90°보다 크고 180°보다 작다',
      '맞꼭지각: 크기가 같다',
    ],
    examples: [
      {
        question: '다음 각을 분류하시오.\n① 35°  ② 90°  ③ 127°  ④ 180°',
        answer: '① 예각  ② 직각  ③ 둔각  ④ 평각',
        explanation: '① 0°<35°<90° → 예각\n② 정확히 90° → 직각\n③ 90°<127°<180° → 둔각\n④ 정확히 180° → 평각',
      },
      {
        question: '두 직선이 만나 ∠AOB = 3x+10°, ∠COD = 70°이다.\n맞꼭지각이므로 x의 값을 구하시오.',
        answer: 'x = 20',
        explanation: '맞꼭지각 → 3x+10 = 70\n3x = 60\nx = 20 ✓',
      },
    ],
    practice: {
      question: '두 직선이 한 점에서 만날 때\n① ∠a + ∠b = 180°이고 ∠a = 2∠b이면 ∠a, ∠b를 각각 구하시오.\n② ∠a의 맞꼭지각의 크기는?',
      answer: '① ∠a=120°, ∠b=60°  ② 120°',
      hint: '① ∠a+∠b=180°, ∠a=2∠b → 2∠b+∠b=180°. ② 맞꼭지각=같은 크기',
      steps: [
        '① ∠a=2∠b를 ∠a+∠b=180°에 대입',
        '2∠b+∠b=180° → 3∠b=180° → ∠b=60°',
        '∠a=2×60°=120°',
        '② 맞꼭지각 = ∠a = 120°',
      ],
    },
    misconceptions: ['GE02'],
  },
  {
    id: 'GA03',
    title: '평행선과 교선',
    subtitle: '두 직선의 평행·수직 관계와 기호를 이해한다',
    icon: '🛤️',
    color: 'bg-indigo-500',
    lightColor: 'bg-indigo-50',
    borderColor: 'border-indigo-300',
    textColor: 'text-indigo-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['평행', '수직', '교점', '교선'],
    objective: '평행과 수직의 개념을 이해하고, 관련 기호를 사용할 수 있다.',
    concept: `**평행과 수직**

📌 평행 (Parallel)
• 같은 평면에서 만나지 않는 두 직선
• 기호: l∥m (l은 m에 평행)
• 평행선 사이의 거리: 어디서나 일정

📌 수직 (Perpendicular)
• 90°로 만나는 두 직선
• 기호: l⊥m (l은 m에 수직)
• 수선: 수직으로 만나는 선
• 수선의 발: 수선과 직선의 교점

📌 점과 직선 사이의 거리
• 점에서 직선에 내린 수선의 발까지의 거리
• 가장 짧은 거리

📌 평행선 사이의 거리
• 두 평행선에 공통으로 수직인 선분의 길이
• 어디서 재도 일정

⚠️ 주의
• 평행은 "같은 평면"에서만!
• 공간에서는 꼬인 위치도 있음`,
    keyPoints: [
      '평행: 같은 평면, 만나지 않는 두 직선 (∥)',
      '수직: 90°로 만나는 두 직선 (⊥)',
      '점과 직선의 거리: 수선의 발까지 거리',
      '평행선 사이 거리: 어디서나 일정',
    ],
    examples: [
      {
        question: '다음 중 옳은 것은?\n① 평행한 두 직선은 어느 한 점에서 만난다\n② l⊥m이면 l과 m이 이루는 각은 90°이다\n③ 점에서 직선까지의 거리는 여러 가지이다',
        answer: '② l⊥m이면 이루는 각은 90°',
        explanation: '① 틀림: 평행은 만나지 않음\n② 맞음: 수직은 90°로 만남 ✓\n③ 틀림: 거리는 수선의 발까지로 유일',
      },
      {
        question: '두 평행선 l, m 사이의 거리가 5cm이다.\nl 위의 점 P에서 m까지의 최단 거리는?',
        answer: '5cm',
        explanation: '평행선 사이의 거리는 어디서 재도 일정 → 5cm',
      },
    ],
    practice: {
      question: '직선 l 위에 있지 않은 점 P에서 직선 l에 수선을 내렸더니\n수선의 발이 H이고 PH = 6cm이다.\n① 점 P에서 직선 l까지의 거리는?\n② l 위의 다른 점 Q에 대해 PQ > PH인 이유는?',
      answer: '① 6cm  ② 직각삼각형에서 빗변이 가장 길기 때문',
      hint: '① 점과 직선의 거리 = 수선의 발까지 거리. ② 직각삼각형 빗변 > 다른 변',
      steps: [
        '① 점 P와 직선 l의 거리 = PH = 6cm',
        '② △PHQ에서 ∠PHQ=90°이므로 PQ는 빗변',
        '빗변이 가장 길므로 PQ > PH',
      ],
    },
    misconceptions: ['GE03'],
  },
  {
    id: 'GA04',
    title: '두 직선의 위치 관계',
    subtitle: '공간에서 두 직선의 위치 관계(꼬인 위치 포함)를 이해한다',
    icon: '📦',
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['꼬인 위치', '평행', '공간', '직선'],
    objective: '공간에서 두 직선의 세 가지 위치 관계를 구별하고 꼬인 위치를 이해한다.',
    concept: `**두 직선의 위치 관계**

📌 평면에서 (2가지)
① 한 점에서 만남
② 평행 (만나지 않음)

📌 공간에서 (3가지)
① 한 점에서 만남
② 평행 (같은 평면 위에 있고 만나지 않음)
③ 꼬인 위치 (같은 평면 위에 없고, 만나지도 않음)

📌 꼬인 위치란?
• 공간에서만 존재하는 개념!
• 한 평면 위에 놓을 수 없는 두 직선
• 직육면체에서 예시:
  - AB와 DH → 꼬인 위치
  - AB와 EF → 평행
  - AB와 BC → 한 점에서 만남

⚠️ 핵심 구분
• 만나지 않음 → 평행 또는 꼬인 위치
• 꼬인 위치: 같은 평면 위에 있지 않음!
• 평면에서는 꼬인 위치가 없음`,
    keyPoints: [
      '평면: 만남 또는 평행 (2가지)',
      '공간: 만남, 평행, 꼬인 위치 (3가지)',
      '꼬인 위치: 만나지도 않고 평행하지도 않음',
      '꼬인 위치는 같은 평면에 놓을 수 없음',
    ],
    examples: [
      {
        question: '직육면체 ABCD-EFGH에서\n① 모서리 AB와 평행한 모서리를 모두 구하시오.\n② 모서리 AB와 꼬인 위치에 있는 모서리를 하나 구하시오.',
        answer: '① EF, DC, HG  ② DH (또는 CG, EH, FG 등)',
        explanation: '① AB에 평행: 같은 방향으로 나란한 EF, DC, HG\n② 꼬인 위치: AB와 만나지도 않고 평행하지도 않은 DH ✓',
      },
      {
        question: '다음 중 꼬인 위치가 가능한 경우는?\n① 평면에서 두 직선\n② 공간에서 두 직선\n③ 같은 직선 위의 두 선분',
        answer: '② 공간에서 두 직선',
        explanation: '꼬인 위치는 3차원 공간에서만 존재합니다.',
      },
    ],
    practice: {
      question: '직육면체에서 한 모서리를 기준으로\n① 그 모서리와 수직인 모서리는 몇 개인가요?\n② 그 모서리와 꼬인 위치에 있는 모서리는 몇 개인가요?',
      answer: '① 4개  ② 4개',
      hint: '직육면체의 각 모서리의 방향(가로·세로·높이)을 기준으로 생각하세요.',
      steps: [
        '① 기준 모서리와 직각으로 만나는 모서리: 4개',
        '② 만나지도 않고 평행하지도 않은 모서리: 4개',
        '(나머지는 평행 3개)',
      ],
    },
    misconceptions: ['GE04'],
  },
  {
    id: 'GA05',
    title: '동위각·엇각',
    subtitle: '평행선에서 동위각·엇각·동측 내각의 성질을 이해한다',
    icon: '🔭',
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
    textColor: 'text-teal-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['동위각', '엇각', '동측 내각', '평행선'],
    objective: '평행선과 한 직선이 만날 때 생기는 각의 관계를 이해하고 활용한다.',
    concept: `**동위각·엇각·동측 내각**

📌 두 직선이 한 직선과 만날 때 생기는 각

◆ 동위각 (Corresponding Angles)
• 같은 위치에 있는 각 (F자 모양)
• l∥m이면 동위각의 크기 같음

◆ 엇각 (Alternate Interior Angles)
• 반대쪽에 엇갈려 있는 각 (Z자 모양)
• l∥m이면 엇각의 크기 같음

◆ 동측 내각 (Co-interior Angles)
• 같은 쪽에 있는 두 내각 (C자 모양)
• l∥m이면 동측 내각의 합 = 180°

📌 핵심 정리 (l∥m일 때)
| 종류 | 성질 |
|------|------|
| 동위각 | 크기 같음 |
| 엇각 | 크기 같음 |
| 동측 내각 | 합이 180° |

📌 역도 성립!
• 동위각이 같으면 → 두 직선은 평행
• 엇각이 같으면 → 두 직선은 평행

⚠️ 자주 하는 실수
• 엇각을 보각(합=180°)으로 착각하지 말 것!
• 엇각은 크기가 "같음"`,
    keyPoints: [
      '동위각: 같은 위치, l∥m이면 크기 같음',
      '엇각: 엇갈린 위치, l∥m이면 크기 같음',
      '동측 내각: 같은 쪽, l∥m이면 합이 180°',
      '역: 동위각=같으면 두 직선 평행',
    ],
    examples: [
      {
        question: 'l∥m이고 ∠a = 65°일 때\n① 동위각의 크기\n② 엇각의 크기\n③ 동측 내각의 크기',
        answer: '① 65°  ② 65°  ③ 115°',
        explanation: '① 동위각: l∥m이면 같음 → 65°\n② 엇각: l∥m이면 같음 → 65°\n③ 동측 내각: 합=180° → 180°-65°=115°',
      },
      {
        question: '두 직선 l, m이 직선 n과 만날 때\n동위각이 70°로 같다면 l과 m의 관계는?',
        answer: 'l∥m (평행)',
        explanation: '동위각이 같으면 두 직선은 평행 → l∥m ✓',
      },
    ],
    practice: {
      question: 'l∥m이고, 직선 n이 두 평행선을 가로지른다.\n∠a = 40°, ∠b = 30°일 때\n(∠a, ∠b는 각각 l, m에서 꺾인 선의 안쪽 각)\n꺾인 선의 꼭짓각 ∠x를 구하시오.',
      answer: '∠x = 70°',
      hint: '평행선 사이 꺾인 선: 엇각을 이용해 ∠a와 ∠b를 꼭짓점에 모을 수 있음',
      steps: [
        '꼭짓점을 지나는 l의 평행선을 긋는다',
        '∠a의 엇각: 40° (꼭짓점 아래)',
        '∠b의 엇각: 30° (꼭짓점 위)',
        '∠x = 40° + 30° = 70°',
      ],
    },
    misconceptions: ['GE03', 'GE05'],
  },
]

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────
function PracticePanel({ practice }: { practice: typeof STEPS[0]['practice'] }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-sky-50 border border-sky-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-sky-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-sky-100 text-sky-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-sky-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      {!revealed ? (
        <div className="flex gap-2">
          {lineIdx < story.lines.length - 1 && (
            <button onClick={() => setLineIdx(lineIdx + 1)}
              className="flex-1 py-2 bg-sky-100 hover:bg-sky-200 text-sky-700 font-semibold rounded-xl text-xs transition-colors">
              다음 대화 →
            </button>
          )}
          {lineIdx === story.lines.length - 1 && (
            <button onClick={() => setRevealed(true)}
              className="flex-1 py-2 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1">
              <Sparkles size={12} /> 핵심 공식 보기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
          <p className="text-lg text-center mb-1">{story.revealEmoji}</p>
          <p className="text-xs text-sky-800 font-mono text-center">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id)).slice(0, 3)
  if (visible.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={12} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X size={11} /></button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const TOTAL_STEPS = STEPS.length

interface G1_1LearnPageProps { defaultStepId?: string | null }

export default function G1_1LearnPage({ defaultStepId }: G1_1LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(null)
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')

  const { completeStep, markPracticeAnswered } = useProgressStore(s => ({
    completeStep: s.completeStep,
    markPracticeAnswered: s.markPracticeAnswered,
  }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  // ── 단계 목록 화면 ──────────────────────────────────────────────────────────
  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <PredictedFragmentPanel fragments={predictedFragments} />

        {/* 헤더 */}
        <div className="card bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-sky-100 rounded-2xl flex items-center justify-center text-2xl">📏</div>
            <div>
              <h2 className="text-lg font-black text-slate-800">기본 도형</h2>
              <p className="text-xs text-slate-500">G1-1 · 도형 영역</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-sky-100 rounded-full overflow-hidden">
              <div className="h-2 bg-sky-500 rounded-full transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs font-bold text-sky-600 w-16 text-right">
              {completedCount}/{TOTAL_STEPS} 완료
            </span>
          </div>
        </div>

        {/* 단계 목록 */}
        <div className="space-y-2">
          {STEPS.map((step, idx) => {
            const stepProg = learnProgress.steps[step.id]
            const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${done ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 hover:border-sky-300'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${done ? 'bg-emerald-100' : step.lightColor}`}>
                    {done ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                          <Youtube size={9} /> 영상
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{step.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-400">{step.id}</span>
                    <ChevronRight size={15} className="text-slate-300" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {completedCount === TOTAL_STEPS && (
          <div className="card bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-sm font-bold text-emerald-700">모든 개념 학습 완료!</p>
            <p className="text-xs text-slate-500 mt-1">실전 TEST에 도전해보세요</p>
          </div>
        )}
      </div>
    )
  }

  // ── 단계 상세 화면 ──────────────────────────────────────────────────────────
  const step = selectedStep
  const stepProg = learnProgress.steps[step.id]
  const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
  const story = STEP_STORIES[step.id]
  const video = getApprovedByTag(step.id)

  const tabs = [
    { id: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { id: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { id: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { id: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* 상단 헤더 */}
      <div className={`${step.lightColor} border-b ${step.borderColor} px-4 pt-3 pb-0 flex-shrink-0`}>
        <button onClick={() => setSelectedStep(null)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2 transition-colors">
          ← 목록으로
        </button>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{step.icon}</span>
          <div>
            <h2 className="text-base font-black text-slate-800">{step.title}</h2>
            <p className="text-xs text-slate-500">{step.subtitle}</p>
          </div>
          {done && <CheckCircle size={18} className="text-emerald-500 ml-auto" />}
        </div>
        {/* 태그 */}
        <div className="flex gap-1 flex-wrap mb-2">
          {step.tags.map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 bg-white/80 border border-sky-200 text-sky-700 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
        {/* 탭 */}
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-3 py-2 rounded-t-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-sky-700 border-t border-l border-r border-sky-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'story' && story && <StoryCard story={story} />}

        {activeTab === 'concept' && (
          <div className="space-y-4">
            {/* 학습 목표 */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
              <p className="text-xs font-bold text-sky-700 mb-1 flex items-center gap-1">
                <Target size={11} /> 학습 목표
              </p>
              <p className="text-sm text-slate-700">{step.objective}</p>
            </div>
            {/* 개념 설명 */}
            <div className="card">
              <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                <BookOpen size={11} /> 핵심 개념
              </p>
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {step.concept}
              </div>
            </div>
            {/* 핵심 포인트 */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
              <p className="text-xs font-bold text-sky-700 mb-2 flex items-center gap-1">
                <Star size={11} /> 핵심 포인트
              </p>
              <ul className="space-y-1.5">
                {step.keyPoints.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="text-sky-500 font-bold flex-shrink-0">✓</span>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
            {/* 영상 링크 */}
            {video && (
              <button onClick={() => setVideoModal({ stepId: step.id, label: step.title })}
                className="w-full flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                <Youtube size={16} className="text-red-600" />
                <span className="text-sm font-semibold text-red-700">개념 설명 영상 보기</span>
                <ArrowRight size={13} className="text-red-400 ml-auto" />
              </button>
            )}
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-500">📝 예제 문제</p>
            {step.examples.map((ex, i) => (
              <ExampleCard key={i} example={ex} idx={i} />
            ))}
          </div>
        )}

        {activeTab === 'practice' && (
          <PracticePanel practice={step.practice} />
        )}

        {/* 완료 버튼 */}
        {!done ? (
          <button onClick={() => handleComplete(step)}
            className="w-full py-3 bg-sky-500 hover:bg-sky-600 text-white font-black rounded-2xl transition-colors flex items-center justify-center gap-2">
            <CheckCircle size={16} /> 학습 완료
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-2xl">
            <CheckCircle size={16} className="text-emerald-500" />
            <span className="text-sm font-bold text-emerald-700">완료된 단계입니다</span>
          </div>
        )}
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
